/**
 * HTTP service for the board feature (PX-W7.4, TASK-41CE1DE2): the project's
 * tasks, backlog and epics, the artefact under review, the run log, and the six
 * review-gate verbs. One transport (`apiClient` from `@/services`).
 *
 * `getArtefactContent` is the ONE call on this service whose response is not
 * JSON — `GET tasks/:taskId/:kind-content` answers `text/markdown;
 * charset=utf-8` (`BoardController.getArtefactContent`). It is typed
 * `Promise<string>` and never given a JSON type parameter that implies
 * parsing (Decision 5 / INV-4.4); axios's default response transform only
 * attempts `JSON.parse` and falls back to the raw string when that throws,
 * so markdown always survives unparsed here.
 */

import { apiClient } from '@/services'
import type {
  ActivityListResponse,
  ArtefactKindSuffix,
  BoardEpicRow,
  BoardTaskRow
} from '../types'

const base = (orgId: string, projectId: string): string => `/${orgId}/projects/${projectId}`

/** Body of the three feedback-carrying verdict endpoints (`FeedbackRequest`, non-blank). */
export interface FeedbackInput {
  feedback: string
}

export const boardService = {
  /** `GET …/tasks` — every task on the board, active + backlog + archive is excluded (see epic AC). */
  async listTasks(orgId: string, projectId: string): Promise<BoardTaskRow[]> {
    return apiClient.get<BoardTaskRow[]>(`${base(orgId, projectId)}/tasks`)
  },

  /**
   * `GET …/backlog` — the BACKLOG pool `listTasks` excludes, where generated
   * proposals land (CR-BUILD-BOARD-EPICS-2026-09-26, spec R.6.1).
   */
  async listBacklog(orgId: string, projectId: string): Promise<BoardTaskRow[]> {
    return apiClient.get<BoardTaskRow[]>(`${base(orgId, projectId)}/backlog`)
  },

  /**
   * `POST …/board/pull/:taskId` — a named human pulls one BACKLOG task onto the
   * active board; the server answers the row, now `todo` (Pull to board, fix
   * cycle 1 — reverses Decision D5). Project WRITE, gated server-side; a task
   * no longer in the backlog answers 404.
   */
  async pullFromBacklog(orgId: string, projectId: string, taskId: string): Promise<BoardTaskRow> {
    return apiClient.post<BoardTaskRow>(`${base(orgId, projectId)}/board/pull/${taskId}`)
  },

  /** `GET …/tasks/:taskId` — one task. */
  async getTask(orgId: string, projectId: string, taskId: string): Promise<BoardTaskRow> {
    return apiClient.get<BoardTaskRow>(`${base(orgId, projectId)}/tasks/${taskId}`)
  },

  /** `PUT …/tasks/:taskId` — sends only the edited fields (wire rule: never a full re-post). */
  async updateTask(
    orgId: string,
    projectId: string,
    taskId: string,
    input: Partial<BoardTaskRow>
  ): Promise<BoardTaskRow> {
    return apiClient.put<BoardTaskRow>(`${base(orgId, projectId)}/tasks/${taskId}`, input)
  },

  /** `GET …/epics` — modules (a PX module is an epic with tasks). */
  async listEpics(orgId: string, projectId: string): Promise<BoardEpicRow[]> {
    return apiClient.get<BoardEpicRow[]>(`${base(orgId, projectId)}/epics`)
  },

  /**
   * `GET …/tasks/:taskId/:kind-content` — raw markdown for one artefact kind.
   * `kind` is the SUFFIX (`plan` | `spec` | `code-changes` | `tests`); the
   * `-content` segment is appended here and nowhere else, so a bare kind is
   * never sent (Decision 5, pinned by `boardContract.test.ts`).
   */
  async getArtefactContent(
    orgId: string,
    projectId: string,
    taskId: string,
    kind: ArtefactKindSuffix
  ): Promise<string> {
    return apiClient.get<string>(`${base(orgId, projectId)}/tasks/${taskId}/${kind}-content`)
  },

  /**
   * `GET …/activity?entityType=taskId` — the PROJECT-scoped activity route,
   * unconditionally (Decision 1). Returns every task-context activity row
   * for the whole project, envelope `{items, nextCursor}`; the caller
   * narrows to ONE task client-side (`selectTaskActivity`). One fetch per
   * project, cached, rather than one per task.
   */
  async listTaskActivity(orgId: string, projectId: string): Promise<ActivityListResponse> {
    return apiClient.get<ActivityListResponse>(
      `${base(orgId, projectId)}/activity?entityType=taskId`
    )
  },

  /** `POST …/tasks/:taskId/approve-spec` — no body; spec → plan. */
  async approveSpec(orgId: string, projectId: string, taskId: string): Promise<BoardTaskRow> {
    return apiClient.post<BoardTaskRow>(`${base(orgId, projectId)}/tasks/${taskId}/approve-spec`)
  },

  /** `POST …/tasks/:taskId/request-spec-changes` — `{feedback}` non-blank. */
  async requestSpecChanges(
    orgId: string,
    projectId: string,
    taskId: string,
    input: FeedbackInput
  ): Promise<BoardTaskRow> {
    return apiClient.post<BoardTaskRow>(
      `${base(orgId, projectId)}/tasks/${taskId}/request-spec-changes`,
      input
    )
  },

  /** `POST …/tasks/:taskId/approve` — no body; uat → done. */
  async approve(orgId: string, projectId: string, taskId: string): Promise<BoardTaskRow> {
    return apiClient.post<BoardTaskRow>(`${base(orgId, projectId)}/tasks/${taskId}/approve`)
  },

  /** `POST …/tasks/:taskId/request-changes` — `{feedback}` non-blank. */
  async requestChanges(
    orgId: string,
    projectId: string,
    taskId: string,
    input: FeedbackInput
  ): Promise<BoardTaskRow> {
    return apiClient.post<BoardTaskRow>(
      `${base(orgId, projectId)}/tasks/${taskId}/request-changes`,
      input
    )
  },

  /** `POST …/tasks/:taskId/complete-testing` — no body; test → uat. */
  async completeTesting(orgId: string, projectId: string, taskId: string): Promise<BoardTaskRow> {
    return apiClient.post<BoardTaskRow>(
      `${base(orgId, projectId)}/tasks/${taskId}/complete-testing`
    )
  },

  /** `POST …/tasks/:taskId/reject-testing` — `{feedback}` non-blank. */
  async rejectTesting(
    orgId: string,
    projectId: string,
    taskId: string,
    input: FeedbackInput
  ): Promise<BoardTaskRow> {
    return apiClient.post<BoardTaskRow>(
      `${base(orgId, projectId)}/tasks/${taskId}/reject-testing`,
      input
    )
  }
}
